/**
 * The calls behind « Programmer » (PLAN-MAIL-ENVOI-PROGRAMME). Every gesture goes through an RPC
 * that re-checks the caller (admin session) and the rules in base; each answers null on success,
 * else the sentence to show — the same contract as the composer's `submit`.
 */
import { z } from 'zod';

import type { Json } from '../../types/supabase';
import { logWarn } from '../logger';
import { logReadFailure, ReadFailure } from '../net/logReadFailure';
import { supabase } from '../supabase';
import {
  readScheduledSends,
  scheduleRefusalMessage,
  type ScheduledMailSend,
  type ScheduleTiming,
} from './scheduledSend';

const answerSchema = z.object({ ok: z.boolean(), code: z.string().optional() });
const suggestionSchema = z.object({ send_at: z.string(), reason: z.string() });
/** What a list reader shows when the scheduled sends cannot be read. */
const UNREADABLE = 'Envois programmés illisibles.';

function refusal(rpc: string, data: unknown, error: { message: string } | null): string | null {
  if (error) {
    logWarn('relations', 'Scheduled send RPC failed', {
      rpc,
      message: error.message,
    });
    return error.message.includes('reserved to admins')
      ? 'Réservé aux administrateurs.'
      : 'Action impossible. Réessaie.';
  }
  const answer = answerSchema.safeParse(data);
  if (!answer.success) {
    logWarn('relations', 'Scheduled send RPC answer unreadable', { rpc });
    return 'Action impossible. Réessaie.';
  }
  return answer.data.ok ? null : scheduleRefusalMessage(answer.data.code);
}

/** The gmail-send body, frozen at the click. `asDraft` is set by the server (real send). */
export type ScheduledPayload = Record<string, unknown>;

export async function scheduleMailSend(input: {
  payload: ScheduledPayload;
  sendAt: string;
  timing: ScheduleTiming;
  aiDraftId: string | null;
}): Promise<string | null> {
  const { data, error } = await supabase.rpc('schedule_mail_send', {
    p_payload: input.payload as Json,
    p_send_at: input.sendAt,
    p_timing: { ...input.timing } as Json,
    p_ai_draft_id: input.aiDraftId ?? undefined,
  });
  return refusal('schedule_mail_send', data, error);
}

export async function cancelScheduledSend(id: string): Promise<string | null> {
  const { data, error } = await supabase.rpc('cancel_scheduled_mail_send', {
    p_id: id,
  });
  return refusal('cancel_scheduled_mail_send', data, error);
}

/** A new time, or now (`sendAt` null): a new decision, which also clears a hold. */
export async function rescheduleSend(
  id: string,
  sendAt: string | null,
  timing: ScheduleTiming | null,
): Promise<string | null> {
  const { data, error } = await supabase.rpc('reschedule_mail_send', {
    p_id: id,
    p_send_at: sendAt ?? undefined,
    p_timing: timing ? ({ ...timing } as Json) : undefined,
  });
  return refusal('reschedule_mail_send', data, error);
}

export interface SendTimeSuggestion {
  sendAt: string;
  reason: string;
}

/** « Au meilleur moment »: a rule in base (the contact's habits, else 8 h 30 a working day). */
export async function suggestSendTime(recipients: readonly string[]): Promise<SendTimeSuggestion | null> {
  const { data, error } = await supabase.rpc('suggest_mail_send_time', {
    p_recipients: [...recipients],
  });
  if (error) logReadFailure('relations', 'Best send time unavailable', error);
  // null = no working day found within two weeks: the person picks a date.
  const parsed = suggestionSchema.safeParse(data);
  return parsed.success ? { sendAt: parsed.data.send_at, reason: parsed.data.reason } : null;
}

export async function listScheduledSends(threadKey: string | null): Promise<ScheduledMailSend[]> {
  const { data, error } = await supabase.rpc('list_scheduled_mail_sends', {
    p_thread_key: threadKey ?? undefined,
    p_include_done: false,
  });
  // Thrown, not logged: the reader that catches it traces it once, with this cause.
  if (error) throw new ReadFailure(UNREADABLE, error);
  const { sends, unreadable } = readScheduledSends(data);
  if (unreadable > 0)
    logWarn('relations', 'Scheduled send rows dropped', {
      threadKey,
      unreadable,
    });
  return sends;
}

// `list_mission_scheduled_sends` is newer than the generated types: the call is typed here, its rows by Zod.
type MissionSendsRpc = (fn: 'list_mission_scheduled_sends', args: { p_thread_id: string }) => Promise<{
  data: unknown;
  error: { message: string } | null;
}>;

/** The live scheduled sends whose draft belongs to a mission (`list_mission_scheduled_sends`). */
export async function listMissionScheduledSends(missionThreadId: string): Promise<ScheduledMailSend[]> {
  const { data, error } = await (supabase.rpc as unknown as MissionSendsRpc)('list_mission_scheduled_sends', {
    p_thread_id: missionThreadId,
  });
  if (error) throw new ReadFailure(UNREADABLE, error);
  const { sends, unreadable } = readScheduledSends(data);
  if (unreadable > 0) logWarn('relations', 'Mission scheduled send rows dropped', { missionThreadId, unreadable });
  return sends;
}
